import { useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import CheckoutAlert from '../CheckoutAlert';
import { TextField, buttonClass } from '../CheckoutControls';
import { validatePaymentStep } from '@/lib/checkout/validation';
import type { FieldErrors, PaymentDraft } from '@/lib/checkout/types';

interface PaymentStepProps {
  value: PaymentDraft;
  onChange: (patch: Partial<PaymentDraft>) => void;
  onNext: () => void;
  onBack: () => void;
  /** Set when the charge attempt on step 5 failed, so the member can retry here. */
  failure: { title: string; message: string } | null;
  onDismissFailure: () => void;
}

/** Step 4 — card capture. The card is charged on step 5, after the agreement. */
export default function PaymentStep({
  value,
  onChange,
  onNext,
  onBack,
  failure,
  onDismissFailure,
}: PaymentStepProps) {
  const [submitted, setSubmitted] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Errors are derived once they've tried to continue, so they clear as fields are fixed.
  const errors: FieldErrors = submitted ? validatePaymentStep(value) : {};
  const errorList = Object.keys(errors);

  const submit = () => {
    setSubmitted(true);

    if (Object.keys(validatePaymentStep(value)).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    onDismissFailure();
    onNext();
  };

  return (
    <>
      {failure ? (
        <CheckoutAlert
          tone="error"
          title={failure.title}
          actions={
            <button
              type="button"
              onClick={() => document.getElementById('payment-card-number')?.focus()}
              className={buttonClass('sm')}
            >
              Use a different card
            </button>
          }
        >
          {failure.message} Nothing has been charged. Your login and contact details are saved — just
          update your payment method below.
        </CheckoutAlert>
      ) : null}

      <h2 className="m-0 mb-1.5 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">Payment</h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[#7a6a8c]">
        Enter your payment details to complete your membership. You’ll review the full total before
        anything is charged.
      </p>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          id="payment-card-number"
          label="Card number"
          required
          inputMode="numeric"
          autoComplete="cc-number"
          placeholder="1234 1234 1234 1234"
          value={value.cardNumber}
          error={errors.cardNumber}
          onChange={(event) => onChange({ cardNumber: event.target.value })}
        />

        <div className="flex flex-col gap-0 sm:flex-row sm:gap-3">
          <TextField
            id="payment-expiry"
            label="Expiration"
            required
            className="sm:flex-1"
            autoComplete="cc-exp"
            placeholder="MM / YY"
            value={value.expiry}
            error={errors.expiry}
            onChange={(event) => onChange({ expiry: event.target.value })}
          />
          <TextField
            id="payment-cvc"
            label="CVC"
            required
            className="sm:flex-1"
            autoComplete="cc-csc"
            placeholder="123"
            value={value.cvc}
            error={errors.cvc}
            onChange={(event) => onChange({ cvc: event.target.value })}
          />
          <TextField
            id="payment-zip"
            label="ZIP Code"
            required
            className="sm:flex-1"
            inputMode="numeric"
            autoComplete="billing postal-code"
            value={value.zip}
            error={errors.zip}
            onChange={(event) => onChange({ zip: event.target.value })}
          />
        </div>

        <p className="mb-4 flex items-center gap-1.5 text-[11.5px] text-[#7a6a8c]">
          <Lock aria-hidden="true" className="h-3.5 w-3.5" />
          Card details are captured by Stripe and never stored on our servers.
        </p>

        {errorList.length > 0 ? (
          <div ref={summaryRef} tabIndex={-1}>
            <CheckoutAlert tone="error" title="Check the highlighted fields." compact>
              {errorList.length === 1
                ? '1 field needs attention before you can continue.'
                : `${errorList.length} fields need attention before you can continue.`}
            </CheckoutAlert>
          </div>
        ) : null}

        <CheckoutAlert tone="info" title="Your progress is saved." compact live={false}>
          Steps 1–3 are complete — you won’t need to re-enter your login or contact information.
        </CheckoutAlert>

        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onBack} className={buttonClass('outline')}>
            Back
          </button>
          <button type="submit" className={buttonClass('primary')}>
            {failure ? 'Retry payment' : 'Next'}
          </button>
        </div>
      </form>
    </>
  );
}
